import { FileChannel } from 'src/store/files/types';
import {
  EsigRequestCompleteNotification,
  EsigRequestNotification,
  NotificationsState,
} from 'src/store/notifications/types';

/**
 *
 * @param notificationsState contains all notifications loaded into state
 * @returns sorted channel options based on lastNotification, which should always be present on options using this sort function
 */
export const fileChannelsSortFunction =
  (notificationsState: NotificationsState) =>
  (a: FileChannel, b: FileChannel) => {
    let esigNotificationA:
      | EsigRequestNotification
      | EsigRequestCompleteNotification
      | undefined;
    let esigNotificationB:
      | EsigRequestNotification
      | EsigRequestCompleteNotification
      | undefined;

    [
      ...notificationsState.esigRequestNotifications,
      ...notificationsState.esigRequestCompleteNotifications,
    ].forEach((esigNotif) => {
      // esig notifications are not set as lastNotification and if they match a channel they overwrite other notifications set
      if (a?.id === esigNotif.fields?.channelId) {
        esigNotificationA = esigNotif;
      }
      if (b?.id === esigNotif.fields?.channelId) {
        esigNotificationB = esigNotif;
      }
    });

    // first we see if esig notifications (which do not appear in lastNotification) exist, for sorting
    // we then get the "last activity" of A and B, defined by the lastNotification updated date (if it exists), otherwise created date
    const lastNotifA = esigNotificationA || a?.fields.lastNotification;
    const lastActivityDateA = lastNotifA
      ? lastNotifA.updatedDate
      : a?.createdDate;

    const lastNotifB = esigNotificationB || b?.fields.lastNotification;
    const lastActivityDateB = lastNotifB
      ? lastNotifB.updatedDate
      : b?.createdDate;
    if (lastActivityDateA && lastActivityDateB) {
      return lastActivityDateA > lastActivityDateB ? -1 : 1;
    }
    return 0;
  };

/**
 * AG Grid comparator for the file table's name column. Numeric collation keeps
 * "2" before "10", and base sensitivity ignores case and accents so ordering
 * matches what users expect from a file browser.
 */
export const compareFileNames = (
  valA?: { name?: string } | null,
  valB?: { name?: string } | null,
) => {
  if (!valA?.name || !valB?.name) {
    return 0;
  }

  return valA.name.localeCompare(valB.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};
